import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { getImageUrl } from '../utils/imageUtils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Settings, Users, Trash2, Shield, Link2, LogOut, UserX, UserCheck } from 'lucide-react';
import { applyCustomFont } from '../utils/fontUtils'; // Import the shared utility

const RoomSettingsModal = ({ open, onClose, room, userRole, members, onUpdate, currentUserId }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  // Check actual ownership (room.owner_id) vs elevated role (userRole)
  const isActualOwner = room?.owner_id === currentUserId;
  const [settings, setSettings] = useState({
    name: room?.name || '',
    description: room?.description || '',
    rules: room?.rules || '',
    theme: room?.theme || {},
    background_url: room?.background_url || ''
  });
  const [inviteLink, setInviteLink] = useState(null);
  const [customFonts, setCustomFonts] = useState([]);
  const [roomTheme, setRoomTheme] = useState({
    font_family: '',
    font_color: '#000000'
  });
  const [bannedUsers, setBannedUsers] = useState([]);
  const [loadingBanned, setLoadingBanned] = useState(false);

  useEffect(() => {
    if (room) {
      setSettings({
        name: room.name || '',
        description: room.description || '',
        rules: room.rules || '',
        theme: room.theme || {},
        background_url: room.background_url || ''
      });
      
      loadInviteLink();

    }
  }, [room]);

  useEffect(() => {
    // Fetch custom fonts when the Fonts tab is opened
    if (activeTab === 'fonts' && room) {
      const fetchFontsAndTheme = async () => {
        const fonts = await loadCustomFonts();
        await loadRoomTheme(fonts);
      };
      fetchFontsAndTheme();
    }
  }, [activeTab, room]);

  // Load banned users when modal opens
  useEffect(() => {
    if (open && room && (userRole === 'owner' || userRole === 'admin')) {
      loadBannedUsers();
    }
  }, [open, room, userRole]);

  const loadBannedUsers = async () => {
    if (!room) return;
    setLoadingBanned(true);
    try {
      const response = await axios.get(`${API}/rooms/${room.id}/banned`);
      setBannedUsers(response.data.banned_users || []);
    } catch (error) {
      console.error('Failed to load banned users:', error);
      setBannedUsers([]);
    } finally {
      setLoadingBanned(false);
    }
  };

  const handleUnban = async (userId) => {
    if (!room) return;
    try {
      await axios.post(`${API}/rooms/${room.id}/unban/${userId}`);
      toast.success('User unbanned successfully');
      loadBannedUsers();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    }
  };

  const loadCustomFonts = async () => {
    try {
      // Fetch custom fonts uploaded by admins
      const response = await axios.get(`${API}/public/fonts`);
      const fonts = response.data || [];
      setCustomFonts(fonts);
      return fonts;
    } catch (error) {
      console.error('Failed to load fonts:', error);
      return [];
    }
  };



  const handleFontChange = (e) => {
    const selectedFontId = e.target.value;
    if (selectedFontId) {
      const font = customFonts.find(f => f.id === selectedFontId);
      if (font && font.url && font.format) { // Ensure font.format exists
        applyCustomFont(font.font_family, font.url, font.format); // Pass format
        setRoomTheme({ 
          ...roomTheme, 
          font_id: font.id,
          font_family: font.font_family 
        });
      }
    } else {
      // Handle "Default Font" selection
      setRoomTheme({ ...roomTheme, font_id: null, font_family: '' });
    }
  };

  const loadRoomTheme = async (fonts) => {
    try {
      const response = await axios.get(`${API}/rooms/${room.id}/theme`);
      const fetchedTheme = response.data;
      setRoomTheme(fetchedTheme);

      if (fetchedTheme.font_id) {
        const selectedFont = fonts.find(f => f.id === fetchedTheme.font_id);
        if (selectedFont && selectedFont.url && selectedFont.format) { // Ensure format exists
          applyCustomFont(selectedFont.font_family, selectedFont.url, selectedFont.format); // Pass format
        } else {
          console.warn(`Font with ID ${fetchedTheme.font_id} not found or missing URL/format in customFonts list.`);
        }
      }
    } catch (error) {
      console.error('Failed to load room theme:', error);
    }
  };

  const saveRoomTheme = async () => {
    try {
      await axios.put(`${API}/rooms/${room.id}/theme`, roomTheme);
      toast.success('Room theme saved successfully!');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save theme');
    }
  };

  const loadInviteLink = async () => {
    // Make sure roomId is valid before calling
    if (!room || !room.id) {
      console.warn('Cannot load invite link: room or room.id is missing.');
      setInviteLink({ invite_code: null }); // Or handle this case appropriately, maybe show a disabled state
      return;
    }

    try {
      // For private rooms, we need to fetch the specific invite code if it exists.
      // For public rooms, we can use room.id if no invite code is generated yet.
      // The backend needs to ensure public rooms can also have invite_codes if desired.
      const response = await axios.get(`${API}/rooms/${room.id}/invite-short`);
      const { token, url, hyperlink } = response.data || {};
      setInviteLink({
        invite_code: token || room.id,
        url: url || `/join/${token || room.id}`,
        hyperlink: hyperlink || `join ${token || room.id}`
      });
    } catch (error) {
      if (error.response && error.response.status === 400) {
        console.warn("Invite link not available for this user or room type.");
        setInviteLink({ invite_code: null }); // Set to null if not available
      } else {
        console.error('Failed to load invite link:', error);
        // Fallback to room ID if no invite link can be fetched (e.g., public room without explicit code)
        setInviteLink({ invite_code: room.id });
      }
    }
  };

  const updateSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/rooms/${room.id}/settings`, settings);
      toast.success('Room settings updated!');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const assignRole = async (userId, role) => {
    try {
      await axios.post(`${API}/rooms/${room.id}/assign-role?user_id=${userId}&role=${role}`);
      toast.success(`Role '${role}' assigned successfully`);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign role');
    }
  };

  const wipeRoom = async () => {
    if (!window.confirm('Are you sure you want to delete ALL messages in this room? This cannot be undone!')) return;
    
    try {
      await axios.delete(`${API}/rooms/${room.id}/wipe`);
      toast.success('Room wiped successfully');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to wipe room');
    }
  };

  const deleteRoom = async () => {
    if (!window.confirm('Are you sure you want to DELETE this room? This cannot be undone!')) return;
    
    try {
      await axios.delete(`${API}/rooms/${room.id}`);
      toast.success('Room deleted successfully');
      onClose();
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete room');
    }
  };

  const copyInviteLink = () => {
    if (inviteLink?.invite_code) {
      const fullInviteUrl = `${window.location.origin}${inviteLink.url || `/join/${inviteLink.invite_code}`}`;
      navigator.clipboard.writeText(fullInviteUrl);
      toast.success('Invite link copied to clipboard!');
    }
  };

  const banUser = async (userId) => {
    if (!window.confirm('Are you sure you want to ban this user?')) return;
    
    try {
      await axios.post(`${API}/rooms/${room.id}/ban/${userId}`);
      toast.success('User banned successfully');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };

  const unbanUser = async (userId) => {
    try {
      await axios.post(`${API}/rooms/${room.id}/unban/${userId}`);
      toast.success('User unbanned successfully');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    }
  };


  const canModerate = ['owner', 'admin', 'moderator'].includes(userRole);
  const canAdmin = ['owner', 'admin'].includes(userRole);
  const isOwner = userRole === 'owner';

  if (!room) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="room-settings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Room Settings: {room.name}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="general" className="w-full" onValueChange={(value) => setActiveTab(value)}>
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="general" data-testid="general-tab">General</TabsTrigger>
            <TabsTrigger value="fonts" data-testid="fonts-tab">Fonts</TabsTrigger>
            <TabsTrigger value="members" data-testid="members-tab">Members</TabsTrigger>
            {(userRole === 'owner' || userRole === 'admin') && (
              <TabsTrigger value="banned" data-testid="banned-tab">Banned</TabsTrigger>
            )}
            {(room.type === 'private' || room.type === 'public') && <TabsTrigger value="invite" data-testid="invite-tab">Invite</TabsTrigger>}
            <TabsTrigger value="danger" data-testid="danger-tab">Danger</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-4">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {canAdmin && (
                  <>
                    <div>
                      <Label>Room Name</Label>
                      <Input
                        value={settings.name}
                        onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                        disabled={!isOwner}
                        data-testid="room-name-input"
                      />
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Input
                        value={settings.description}
                        onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                        data-testid="room-description-input"
                      />
                    </div>

                    <div>
                      <Label>Room Rules</Label>
                      <textarea
                        value={settings.rules}
                        onChange={(e) => setSettings({ ...settings, rules: e.target.value })}
                        className="w-full p-2 border rounded-md min-h-[100px]"
                        placeholder="Set room rules here..."
                        data-testid="room-rules-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Background Image</Label>
                      <div className="space-y-2">
                        <Input
                          value={settings.background_url}
                          onChange={(e) => setSettings({ ...settings, background_url: e.target.value })}
                          placeholder="Enter background URL..."
                          data-testid="background-url-input"
                        />
                        <div className="text-sm text-gray-500">Or upload a file (Max 5MB, jpg/png/gif):</div>
                        <Input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                const token = localStorage.getItem('token');
                                const response = await axios.post(`${API}/media/upload-background`, formData, {
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'multipart/form-data'
                                  }
                                });
                                setSettings({ ...settings, background_url: response.data.url });
                                toast.success('Background uploaded!');
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Upload failed');
                              }
                            }
                          }}
                        />
                      </div>
                    </div>

                    <Button
                      onClick={updateSettings}
                      disabled={loading}
                      className="w-full"
                      data-testid="save-settings-button"
                    >
                      {loading ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </>
                )}

                {!canAdmin && (
                  <div className="text-center py-8 text-gray-500">
                    <Shield className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>You don&apos;t have permission to modify room settings</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Fonts Tab */}
          <TabsContent value="fonts" className="space-y-4">
            <ScrollArea className="h-[400px] pr-4">
              {canAdmin ? (
                <div className="space-y-4">
                  <div>
                    <Label>Choose Font for Room</Label>
                    <select
                      className="w-full p-2 border rounded-md mt-2"
                      value={roomTheme.font_id || ''} // Use font_id for selected state
                      onChange={handleFontChange}
                    >
                      <option value="">Default Font</option>
                      {customFonts.map((font) => (
                        <option key={font.id} value={font.id}> {/* Option value is font.id */}
                          {font.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>Font Color</Label>
                    <Input
                      type="color"
                      value={roomTheme.font_color || '#000000'}
                      onChange={(e) => setRoomTheme({...roomTheme, font_color: e.target.value})}
                      className="mt-2"
                    />
                  </div>

                  <div className="border rounded-lg p-4 bg-gray-50">
                    <Label>Preview</Label>
                    <p
                      style={{
                        fontFamily: roomTheme.font_family || 'inherit',
                        color: roomTheme.font_color || '#000000',
                        fontSize: '16px',
                        marginTop: '8px'
                      }}
                    >
                      This is how your chat room text will look. The quick brown fox jumps over the lazy dog.
                    </p>
                  </div>

                  <Button onClick={saveRoomTheme} className="w-full">
                    Save Font Settings
                  </Button>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  Only room owners and admins can customize fonts
                </p>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Members Management */}
          <TabsContent value="members">
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`member-${member.user_id}`}>
                    <div>
                      <p className="font-medium">{member.user?.display_name || member.user?.username}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
                          {member.role}
                        </Badge>
                        {member.user?.online && (
                          <Badge variant="outline" className="bg-green-50 text-green-700">Online</Badge>
                        )}
                      </div>
                    </div>
                    
                    {canModerate && member.role !== 'owner' && (
                      <div className="flex gap-2">
                        {isOwner && (
                          <>
                            {member.role !== 'admin' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => assignRole(member.user_id, 'admin')}
                                data-testid={`make-admin-${member.user_id}`}
                              >
                                Make Admin
                              </Button>
                            )}
                            {member.role !== 'moderator' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => assignRole(member.user_id, 'moderator')}
                                data-testid={`make-mod-${member.user_id}`}
                              >
                                Make Mod
                              </Button>
                            )}
                          </>
                        )}
                        
                        {member.user?.role !== 'super_admin' && member.user?.role !== 'staff' && (
                          member.status === 'banned' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-300"
                              onClick={() => unbanUser(member.user_id)}
                              data-testid={`unban-${member.user_id}`}
                            >
                              Unban
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => banUser(member.user_id)}
                              data-testid={`ban-${member.user_id}`}
                            >
                              Ban
                            </Button>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Banned Users Tab */}
          {(userRole === 'owner' || userRole === 'admin') && (
            <TabsContent value="banned">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-4 py-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <UserX className="w-4 h-4" />
                    <span>Users banned from this room ({bannedUsers.length})</span>
                  </div>
                  
                  {loadingBanned ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : bannedUsers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <UserCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No banned users in this room</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {bannedUsers.map((banned) => (
                        <div key={banned.user.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                              {banned.user.avatar_url ? (
                                <img src={getImageUrl(banned.user.avatar_url)} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <span className="text-red-600 font-semibold">
                                  {banned.user.username?.substring(0, 2).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{banned.user.display_name || banned.user.username}</p>
                              <p className="text-xs text-gray-500">@{banned.user.username}</p>
                              {banned.banned_by && (
                                <p className="text-xs text-gray-400">
                                  Banned by: {banned.banned_by.username}
                                  {banned.banned_at && ` on ${new Date(banned.banned_at).toLocaleDateString()}`}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUnban(banned.user.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Unban
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          )}

          {/* Invite Link (Private and Public Rooms) */}
          {(room.type === 'private' || room.type === 'public') && (
            <TabsContent value="invite">
              <div className="space-y-4 py-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <Label className="mb-2 block">Invite Code</Label>
                  <div className="flex gap-2">
                    <Input
                      value={inviteLink?.invite_code || room.id || 'Loading...'} // Display invite_code or room.id
                      readOnly
                      data-testid="invite-code-display"
                    />
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <Label className="mb-2 block">Invite Link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={inviteLink?.invite_code ? `${window.location.origin}/join/${inviteLink.invite_code}` : 'Loading...'}
                      readOnly
                      className="flex-1"
                      data-testid="invite-link-display"
                    />
                    <Button
                      onClick={copyInviteLink}
                      disabled={!inviteLink?.invite_code} // Disable if no code to copy
                      data-testid="copy-invite-link-button"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                </div>

                <p className="text-sm text-gray-600">
                  Share this invite link with others to let them join this room.
                </p>
              </div>
            </TabsContent>
          )}

          {/* Danger Zone */}
          <TabsContent value="danger">
            <div className="space-y-4 py-4">
              {canModerate && (
                <div className="p-4 border-2 border-orange-200 bg-orange-50 rounded-lg">
                  <h4 className="font-semibold text-orange-800 mb-2">Wipe Room Messages</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    Delete all messages in this room. This action cannot be undone.
                  </p>
                  <Button
                    variant="destructive"
                    onClick={wipeRoom}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="wipe-room-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Wipe Room Messages
                  </Button>
                </div>
              )}

              {/* Leave Room - Available to all members except actual room owner and Global Chat */}
              {!isActualOwner && room.name !== 'Global Chat' && room.name !== 'public chat' && (
                <div className="p-4 border-2 border-yellow-200 bg-yellow-50 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-2">Leave Room</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    You will no longer have access to this room and its messages.
                  </p>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to leave this room?')) {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.post(`${API}/rooms/${room.id}/leave`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Left room successfully');
                          onClose();
                          onUpdate && onUpdate();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to leave room');
                        }
                      }
                    }}
                    className="border-yellow-600 text-yellow-600 hover:bg-yellow-100"
                    data-testid="leave-room-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Leave Room
                  </Button>
                </div>
              )}

              {isActualOwner && room.name !== 'public chat' && room.name !== 'Global Chat' && (
                <div className="p-4 border-2 border-red-200 bg-red-50 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2">Delete Room</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    Permanently delete this room and all its data. This action cannot be undone.
                  </p>
                  <Button
                    variant="destructive"
                    onClick={deleteRoom}
                    data-testid="delete-room-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Room Permanently
                  </Button>
                </div>
              )}

              {!canModerate && (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>You don&apos;t have permission for these actions</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default RoomSettingsModal;
